<template>
  <div class="comparador-producto">
    <!-- Buscador (se oculta cuando el componente se usa en modal) -->
    <div v-if="!hideSearch" class="search-bar">
      <input
        v-model="searchQuery"
        @input="onSearch"
        type="text"
        placeholder="Buscar producto por código o nombre..."
        class="search-input"
      />
      <ul v-if="searchResults.length > 0" class="search-dropdown">
        <li
          v-for="p in searchResults"
          :key="p.codigo"
          @click="selectProduct(p.codigo)"
          class="search-item"
        >
          <span class="search-code">{{ p.codigo }}</span>
          <span class="search-desc">{{ p.descripcion }}</span>
        </li>
      </ul>
    </div>

    <!-- Loader / Error / Empty state -->
    <div v-if="loading" class="state-box loading">Cargando producto...</div>
    <div v-else-if="error" class="state-box error">{{ error }}</div>
    <div v-else-if="!producto" class="state-box empty">
      Buscá un producto arriba para comparar precios.
    </div>

    <!-- Contenido principal -->
    <template v-else>
      <!-- Header del producto -->
      <div class="product-header">
        <div class="header-left">
          <div class="product-code">CÓDIGO VV — {{ producto.codigo }}</div>
          <h2 class="product-title">{{ producto.descripcion }}</h2>
          <div class="product-meta">
            <span class="vv-price-label">Precio VV:</span>
            <span class="vv-price" :class="vvPriceClass">
              {{ formatMoney(producto.vv_price) }}
            </span>
            <span v-if="vvStatusMessage" class="vv-status-pill" :class="vvStatusClass">
              {{ vvStatusMessage }}
            </span>
            <span v-if="producto.scrape_date" class="scrape-date">
              📅 {{ formatDateTime(producto.scrape_date) }}
            </span>
          </div>
        </div>
        <div class="header-right">
          <div class="vista-toggle">
            <button
              :class="{ active: producto.vista === 'web' }"
              @click="changeVista('web')"
            >Web propia</button>
            <button
              :class="{ active: producto.vista === 'marketplace' }"
              @click="changeVista('marketplace')"
            >Marketplace</button>
          </div>
        </div>
      </div>

      <!-- Tabla de matches -->
      <div class="matches-section">
        <div class="matches-header" v-if="producto.current_matches.length > 10">
          <span class="matches-count">{{ producto.current_matches.length }} tiendas</span>
          <button
            @click="showAllMatches = !showAllMatches"
            class="toggle-all"
          >
            {{ showAllMatches ? 'Ver solo top 10' : `Ver todos` }}
          </button>
        </div>

        <div class="matches-table-wrapper">
          <table class="matches-table">
            <thead>
              <tr>
                <th>Tienda</th>
                <th>Título del match</th>
                <th class="num">Precio</th>
                <th class="num">Diff %</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="m in visibleMatches"
                :key="m.store"
                :class="{}"
              >
                <td>
                  <div class="store-name">{{ displayStoreName(m.store_display) }}</div>
                  <span v-if="m.is_marketplace" class="badge-marketplace">marketplace</span>
                </td>
                <td class="title-cell">{{ m.title || '(sin título)' }}</td>
                <td class="num price-cell">{{ formatMoney(m.price) }}</td>
                <td class="num" :class="diffClass(m.diff_vs_vv_pct)">
                  {{ formatDiff(m.diff_vs_vv_pct) }}
                </td>
                <td>
                  <a v-if="m.url" :href="m.url" target="_blank" rel="noopener">Ver <PhArrowUpRight :size="11" weight="bold" /></a>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="producto.current_matches.length === 0" class="no-matches">
            No hay matches con precio para esta vista.
          </div>
        </div>
      </div>

      <!-- Gráfico histórico -->
      <div class="history-section">
        <div class="history-header">
          <h3>Evolución de precios</h3>
          <div class="range-selector">
            <button
              v-for="r in ['7', '30', '90', 'all']"
              :key="r"
              :class="{ active: producto.rango === r }"
              @click="changeRango(r)"
            >{{ r === 'all' ? 'Todo' : r + 'd' }}</button>
          </div>
        </div>
        <div class="chart-container">
          <LineChart
            v-if="chartData"
            :data="chartData"
            :options="chartOptions"
          />
          <div v-else class="no-chart">Sin datos históricos en este rango.</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Line as LineChart } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title, Tooltip, Legend,
  LineElement, PointElement,
  CategoryScale, LinearScale,
} from 'chart.js'
import axios from 'axios'
import { PhArrowUpRight } from '@phosphor-icons/vue'

const API = "https://dash-visaovip.kompara.tech/api"
const getH = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("token_precios") } })

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale)

// Paleta de colores para las líneas del gráfico (20 colores distinguibles)
const CHART_COLORS = [
  '#1f4e78', '#0a7c2f', '#b42318', '#7c3aed', '#ea580c',
  '#0891b2', '#c026d3', '#65a30d', '#dc2626', '#2563eb',
  '#db2777', '#059669', '#d97706', '#4338ca', '#ca8a04',
  '#be185d', '#15803d', '#9f1239', '#3730a3', '#854d0e',
]

export default {
  name: 'ComparadorProducto',
  components: { LineChart, PhArrowUpRight },
  props: {
    codigo: { type: String, default: null },
    initialVista: { type: String, default: 'web' },
    hideSearch: { type: Boolean, default: false },
  },
  data() {
    return {
      producto: null,
      loading: false,
      error: null,
      currentCodigo: this.codigo,
      currentVista: this.initialVista,
      currentRango: '30',
      showAllMatches: false,
      searchQuery: '',
      searchResults: [],
      searchTimeout: null,
    }
  },
  computed: {
    visibleMatches() {
      if (!this.producto) return []
      if (this.showAllMatches) return this.producto.current_matches
      return this.producto.current_matches.slice(0, 10)
    },
    vvPriceClass() {
      if (!this.producto) return ''
      // Regla simple: verde si VV tiene el mejor precio, rojo en cualquier otro caso
      const isBest = this.producto.vv_position === 'best'
      return {
        'price-best': isBest,
        'price-worst': !isBest && this.producto.current_matches.length > 0,
      }
    },
    vvStatusMessage() {
      if (!this.producto || !this.producto.best_price) return null
      const pos = this.producto.vv_position
      const diff = this.producto.vv_diff_pct_vs_best
      if (pos === 'best') return '✓ Mejor precio del mercado'
      if (diff != null && diff > 0) return `+${diff}% vs el mejor`
      return null
    },
    vvStatusClass() {
      if (!this.producto) return ''
      const isBest = this.producto.vv_position === 'best'
      return {
        'status-good': isBest,
        'status-bad': !isBest,
      }
    },
    chartData() {
      if (!this.producto || !this.producto.price_history) return null
      const h = this.producto.price_history
      if (h.dates.length === 0) return null

      const datasets = []
      // Línea VV (punteada, roja)
      datasets.push({
        label: 'Precio VV',
        data: h.vv_prices,
        borderColor: '#b42318',
        borderDash: [6, 4],
        borderWidth: 2.5,
        backgroundColor: 'transparent',
        tension: 0,
        pointRadius: 4,
        spanGaps: true,
      })

      // Una línea por tienda
      let colorIdx = 0
      for (const store in h.by_store) {
        datasets.push({
          label: this.displayStoreName(this.simplifyStoreName(store)),
          data: h.by_store[store],
          borderColor: CHART_COLORS[colorIdx % CHART_COLORS.length],
          backgroundColor: 'transparent',
          tension: 0.2,
          spanGaps: true,
        })
        colorIdx++
      }

      return { labels: h.dates, datasets }
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${ctx.raw == null ? '—' : this.formatMoney(ctx.raw)}`
            }
          },
        },
        scales: {
          y: { beginAtZero: false, title: { display: true, text: 'Precio' } },
        },
      }
    },
  },
  watch: {
    codigo(newVal) {
      this.currentCodigo = newVal
      this.loadProducto()
    },
  },
  mounted() {
    if (this.currentCodigo) this.loadProducto()
  },
  methods: {
    async loadProducto() {
      if (!this.currentCodigo) return
      this.loading = true
      this.error = null
      try {
        const res = await axios.get(`${API}/comparador/${this.currentCodigo}`, {
          params: { vista: this.currentVista, rango: this.currentRango },
          ...getH()
        })
        this.producto = res.data
        this.showAllMatches = false
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          this.error = 'Sesión expirada. Volvé al dashboard e iniciá sesión nuevamente.'
        } else {
          this.error = err.response?.data?.detail || 'Error cargando el producto'
        }
        this.producto = null
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      clearTimeout(this.searchTimeout)
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = []
        return
      }
      this.searchTimeout = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/comparador`, {
            params: { q: this.searchQuery, vista: this.currentVista, limit: 15 },
            ...getH()
          })
          this.searchResults = res.data
        } catch (e) {
          this.searchResults = []
        }
      }, 300)
    },
    selectProduct(codigo) {
      this.currentCodigo = codigo
      this.searchQuery = ''
      this.searchResults = []
      this.loadProducto()
      // Actualizar URL si hay router
      if (this.$router) {
        this.$router.push({ name: 'producto', params: { codigo } }).catch(() => {})
      }
    },
    changeVista(vista) {
      this.currentVista = vista
      this.loadProducto()
    },
    changeRango(rango) {
      this.currentRango = rango
      this.loadProducto()
    },
    formatMoney(n) {
      if (n == null) return '-'
      return Number(n).toLocaleString('es-PY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDiff(n) {
      if (n == null) return '-'
      const sign = n > 0 ? '+' : ''
      return `${sign}${Number(n).toFixed(1)}%`
    },
    diffClass(n) {
      if (n == null) return ''
      if (n < 0) return 'diff-negative'
      if (n > 0) return 'diff-positive'
      return 'diff-zero'
    },
    formatDateTime(iso) {
      const d = new Date(iso)
      const today = new Date()
      const isToday = d.toDateString() === today.toDateString()
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1)
      const isYesterday = d.toDateString() === yesterday.toDateString()
      const hours = d.getHours().toString().padStart(2, '0')
      const minutes = d.getMinutes().toString().padStart(2, '0')
      if (isToday) return `Hoy ${hours}:${minutes}`
      if (isYesterday) return `Ayer ${hours}:${minutes}`
      return `${d.toLocaleDateString('es-PY')} ${hours}:${minutes}`
    },
    simplifyStoreName(store) {
      // Remueve "_comprasparaguai" para leyendas más limpias
      if (store.endsWith('_comprasparaguai')) {
        return store.replace('_comprasparaguai', ' (mkt)')
      }
      return store
    },
    displayStoreName(s) {
      if (!s) return ''
      return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
  },
}
</script>

<style scoped>
.comparador-producto {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: 'Inter', sans-serif;
  color: var(--color-text);
}

/* ============ BUSCADOR ============ */
.search-bar {
  position: relative;
  margin-bottom: 16px;
}
.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--color-border-strong);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  background: var(--color-surface);
  color: var(--color-text);
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.search-input:focus {
  outline: none;
  border-color: var(--color-accent);
}
.search-input::placeholder {
  color: var(--color-text-faint);
}
.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-top: none;
  border-radius: 0 0 2px 2px;
  max-height: 320px;
  overflow-y: auto;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 10;
  box-shadow: var(--shadow-lg);
}
.search-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  gap: 10px;
  font-size: 12px;
  transition: background 0.1s;
}
.search-item:hover { background: var(--color-hover-row); }
.search-code {
  font-family: var(--font-mono);
  font-weight: 700;
  color: var(--color-accent);
  min-width: 60px;
  font-size: 10.5px;
}
.search-desc {
  color: var(--color-text);
  flex: 1;
}

/* ============ STATES ============ */
.state-box {
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 3px;
  padding: 36px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 12px;
  font-style: italic;
  letter-spacing: 0.03em;
}
.state-box.error {
  background: var(--color-danger-bg);
  color: var(--color-danger-text);
  border: 1px solid var(--color-danger);
  font-style: normal;
}

/* ============ PRODUCT HEADER ============ */
.product-header {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  padding: 12px 16px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
}
.header-left { flex: 1; min-width: 300px; }
.product-code {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--color-accent);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.product-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text);
  margin: 4px 0 10px;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.product-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12px;
}
.vv-price-label {
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 600;
}
.vv-price {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 16px;
  padding: 2px 8px;
  border-radius: 2px;
  font-variant-numeric: tabular-nums;
}
.vv-price.price-best {
  color: var(--color-success-text);
  background: var(--color-success-bg);
  border: 1px solid var(--color-success);
}
.vv-price.price-worst {
  color: var(--color-danger-text);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger);
}
.vv-price.price-middle {
  color: var(--color-accent);
  background: var(--color-accent-bg);
}
.scrape-date {
  color: var(--color-text-faint);
  font-family: var(--font-mono);
  font-size: 10.5px;
}

/* ============ VISTA TOGGLE ============ */
.vista-toggle {
  display: flex;
  gap: 2px;
  background: var(--color-background);
  border: 1px solid var(--color-border-strong);
  padding: 2px;
  border-radius: 2px;
}
.vista-toggle button {
  background: transparent;
  border: none;
  padding: 5px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  transition: all 0.15s;
}
.vista-toggle button:hover {
  color: var(--color-text);
}
.vista-toggle button.active {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
}

/* ============ STATUS PILL ============ */
.vv-status-pill {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 2px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid;
}
.vv-status-pill.status-good {
  background: var(--color-success-bg);
  color: var(--color-success-text);
  border-color: var(--color-success);
}
.vv-status-pill.status-bad {
  background: var(--color-danger-bg);
  color: var(--color-danger-text);
  border-color: var(--color-danger);
}

/* ============ SECCIONES ============ */
.matches-section, .history-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  margin-bottom: 10px;
  overflow: hidden;
}
.matches-header, .history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
  flex-wrap: wrap;
  gap: 12px;
}
.matches-header h3, .history-header h3 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.toggle-all {
  background: transparent;
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.toggle-all:hover {
  color: var(--color-accent);
  border-color: var(--color-accent-border);
}
.matches-count {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}

/* ============ TABLA MATCHES ============ */
.matches-table-wrapper {
  overflow-x: auto;
}
.matches-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-mono);
}
.matches-table th {
  padding: 7px 12px;
  text-align: left;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  background: var(--color-surface-2);
  border-bottom: 1px solid var(--color-border-strong);
}
.matches-table td {
  padding: 8px 12px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}
.matches-table tr:hover { background: var(--color-hover-row); }
.matches-table tr:last-child td { border-bottom: none; }
.matches-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.matches-table .title-cell {
  max-width: 400px;
  color: var(--color-text-muted);
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.matches-table .price-cell {
  font-weight: 700;
  color: var(--color-text);
}
.matches-table a {
  color: var(--color-accent);
  text-decoration: none;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.matches-table a:hover { text-decoration: underline; }

.store-name {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: var(--color-text);
}
.badge-marketplace {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  background: var(--color-leader-bg);
  color: var(--color-leader-text);
  border: 1px solid var(--color-leader);
  border-radius: 2px;
  margin-top: 2px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.diff-negative {
  color: var(--color-success-text);
  font-weight: 700;
}
.diff-positive {
  color: var(--color-danger-text);
  font-weight: 700;
}
.diff-zero {
  color: var(--color-text-muted);
}

.no-matches, .no-chart {
  padding: 36px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  font-style: italic;
}

/* ============ GRÁFICO ============ */
.range-selector {
  display: flex;
  gap: 2px;
  background: var(--color-background);
  border: 1px solid var(--color-border-strong);
  padding: 2px;
  border-radius: 2px;
}
.range-selector button {
  background: transparent;
  border: none;
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  transition: all 0.15s;
}
.range-selector button:hover {
  color: var(--color-text);
}
.range-selector button.active {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
}
.chart-container {
  position: relative;
  height: 360px;
  padding: 12px 16px 16px;
}
</style>